import { Navigate, Params, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { FC, useState } from "react";
import { molliePlatformApiUrl } from "app/constants";
import { PublicInvoiceData } from "app/types.ts";
import { useEffectOnce } from "react-use";
import { StateWithFooter } from "@mollie/revenue-collection-components";
import { NotFoundState, UnexpectedErrorState } from "app/components";
import { messages } from "app/i18n";

enum Error {
  NotFound = "Invoice not found",
  BadResponse = "Bad response from server",
}

export const InvoiceLandingPage: FC = () => {
  const intl = useIntl();
  const [invoiceData, setInvoiceData] = useState<PublicInvoiceData | null>(
    null,
  );
  const [error, setError] = useState<Error | null>(null);
  const params: Params<string> = useParams();

  useEffectOnce(() => {
    const apiUrl = new URL(
      `invoice-ar/invoice/${params.invoiceReferenceId}`,
      molliePlatformApiUrl,
    );

    fetch(apiUrl)
      .then((response) => {
        if (!response.ok) {
          if (response.status === 404) {
            return setError(Error.NotFound);
          } else {
            return setError(Error.BadResponse);
          }
        }

        return response.json().then((data: PublicInvoiceData) => {
          setInvoiceData(data);
        });
      })
      .catch(() => {
        return setError(Error.BadResponse);
      });
  });

  if (error === Error.NotFound) {
    return <NotFoundState />;
  }
  if (error === Error.BadResponse) {
    return <UnexpectedErrorState />;
  }

  if (!invoiceData) {
    return (
      <StateWithFooter
        title={intl.formatMessage(messages.invoiceLandingLoadingTitle)}
        variant="loading"
        description={intl.formatMessage(messages.invoiceLandingLoadingMessage)}
      />
    );
  }

  if (invoiceData.status === "paid") {
    return (
      <StateWithFooter
        title={intl.formatMessage(messages.invoiceIsPaidWithInvoiceNumber, {
          invoiceNumber: invoiceData.invoiceNumber,
        })}
        variant="success"
      />
    );
  }

  if (!invoiceData.isPayable) {
    return (
      <StateWithFooter
        title={intl.formatMessage(
          messages.invoicePaymentErrorInvoiceIsNotPayableWithInvoiceNumber,
          {
            invoiceNumber: invoiceData.invoiceNumber,
          },
        )}
        variant="error"
        description={intl.formatMessage(
          messages.invoicePaymentErrorInvoiceIsNotPayableDescription,
        )}
      />
    );
  }

  return (
    <Navigate
      to={`/${params.lang}/invoice/${invoiceData.invoiceReferenceId}/pay`}
      replace={true}
    />
  );
};
